import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import produce from 'immer';
import { catchError, Observable, tap } from 'rxjs';
import { ApiNotificationItem, CloseMethod, mapNotifications, Notification } from '../../models';
import { ApiService, EnvService, SentryService } from '../../services';
import {
  ApiStoreData,
  defaultApiStoreData,
  defaultApiStoreIterableData,
  InvalidApiResponseDataError,
  MissingApiRequestDataError,
} from '../utils';
import {
  FetchNotifications,
  RespondToNotification,
  SetCgm90Enrollment,
  UpdateNotifications,
} from './notifications.actions';
import { remove } from 'lodash-es';
import { PatientState } from '../patient/patient.state';

export interface NotificationsStateModel {
  notifications: ApiStoreData<Notification[]>;
  respondToNotification: ApiStoreData<null>;
  updateNotifications: ApiStoreData<null>;
  setCgm90Enrollment: ApiStoreData<null>;
}

@State<NotificationsStateModel>({
  name: 'notifications',
  defaults: {
    notifications: defaultApiStoreIterableData,
    respondToNotification: defaultApiStoreData,
    updateNotifications: defaultApiStoreData,
    setCgm90Enrollment: defaultApiStoreData,
  },
})
@Injectable({ providedIn: 'root' })
export class NotificationsState {
  constructor(
    private apiService: ApiService,
    private store: Store,
    private sentry: SentryService,
    private envService: EnvService
  ) {}

  handleError = this.sentry.handleError;
  handleSuccess = this.sentry.handleSuccess;

  @Selector()
  static notificationsState(state: NotificationsStateModel): ApiStoreData<Notification[]> {
    return state.notifications;
  }

  @Selector()
  static respondToNotificationState(state: NotificationsStateModel): ApiStoreData<null> {
    return state.respondToNotification;
  }

  @Selector()
  static setCGM90EnrollmentState(state: NotificationsStateModel): ApiStoreData<null> {
    return state.setCgm90Enrollment;
  }

  @Action(FetchNotifications)
  fetchNotifications(ctx: StateContext<NotificationsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.notifications.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'notifications' });
    }

    return this.apiService.getNotifications(patientId).pipe(
      tap({
        next: (response) => {
          response.data.forEach((n) => {
            this.validateNotification(n);
          });

          const data = mapNotifications(response.data);
          this.handleSuccess({ ctx, data, stateKey: 'notifications' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'notifications' }))
    );
  }

  @Action(RespondToNotification)
  respondToNotification(ctx: StateContext<NotificationsStateModel>, action: RespondToNotification) {
    ctx.setState(
      produce((draft) => {
        draft.respondToNotification.isLoading = true;
        remove(draft.notifications.data, (n) => n.id === action.notificationId);
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'respondToNotification' });
    }

    return this.apiService
      .respondToNotification(patientId, action.notificationId, action.responseId, action.textResponse)
      .pipe(
        tap({
          next: () => {
            this.handleSuccess({ ctx, stateKey: 'respondToNotification' });
          },
        }),
        catchError(
          (error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'respondToNotification' })
        )
      );
  }

  @Action(UpdateNotifications)
  updateNotifications(ctx: StateContext<NotificationsStateModel>) {
    ctx.setState(
      produce((draft) => {
        draft.updateNotifications.isUpdating = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'updateNotifications', updating: true });
    }

    return this.apiService.getNotifications(patientId).pipe(
      tap({
        next: (response) => {
          response.data.forEach((n) => {
            this.validateNotification(n);
          });
          const updateStateFunction = (draft: NotificationsStateModel) => {
            draft.notifications.data = mapNotifications(response.data);
          };

          this.handleSuccess({ ctx, stateKey: 'updateNotifications', updating: true, updateStateFunction });
        },
      }),
      catchError((error: Error): Observable<any> => {
        return this.handleError({ ctx, error, stateKey: 'updateNotifications', updating: true });
      })
    );
  }

  @Action(SetCgm90Enrollment)
  setCGM90Enrollment(ctx: StateContext<NotificationsStateModel>, action: SetCgm90Enrollment) {
    ctx.setState(
      produce((draft) => {
        draft.setCgm90Enrollment.isLoading = true;
      })
    );

    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);

    if (!patientId) {
      const error = new MissingApiRequestDataError(['Missing patient id']);
      return this.handleError({ ctx, error, stateKey: 'setCgm90Enrollment' });
    }

    const body = { patientId: patientId, notificationId: action.notificationId, consented: action.consented };

    return this.apiService.setCgm90Enrollment(patientId, body).pipe(
      tap({
        next: () => {
          this.handleSuccess({ ctx, stateKey: 'setCgm90Enrollment' });
        },
      }),
      catchError((error: Error): Observable<any> => this.handleError({ ctx, error, stateKey: 'setCgm90Enrollment' }))
    );
  }

  validateNotification(notification: ApiNotificationItem) {
    if (!notification.id) {
      throw new InvalidApiResponseDataError(['Invalid notification fields found']);
    }
    if (notification.closeMethod === CloseMethod.Display || notification.closeMethod === CloseMethod.Expiration) {
      if (!notification.externalText) {
        throw new InvalidApiResponseDataError(['Invalid notification fields found']);
      } else if (
        notification.externalButtonLabel1 ||
        notification.externalButtonLabel2 ||
        notification.externalCustomActionId
      ) {
        throw new InvalidApiResponseDataError(['Invalid notification fields found']);
      }
    }
    if (notification.closeMethod === CloseMethod.Action) {
      if (!notification.externalCustomActionId) {
        if (!notification.externalText || !notification.externalButtonLabel1) {
          throw new InvalidApiResponseDataError(['Invalid notification fields found']);
        }
      } else if (notification.externalCustomActionId == 'CGM90ENROLL') {
        if (notification.externalText || notification.externalButtonLabel1 || notification.externalButtonLabel2) {
          throw new InvalidApiResponseDataError(['Invalid notification fields found']);
        }
      } else if (notification.externalCustomActionId == 'TEXT_RESPONSE') {
        if (!notification.externalText || !notification.externalButtonLabel1) {
          throw new InvalidApiResponseDataError(['Invalid notification fields found']);
        }
      }
    }
  }
}
